import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import { useNavigate } from 'react-router-dom';
import { PlateGrid, Plate } from '@ofself/shell';
import api from '../services/api';
import { Badge, EmptyState, Button } from '../components/common';
import './DeveloperOverview.css';
import FleetScores from './FleetScores';

// The series palette — earthy like the fleet's periodic hues, but stepped and
// ORDERED so every adjacent pair stays apart under normal vision and CVD
// (validated with the six-checks palette validator, light AND dark surface).
// Fixed order, assigned by entity — never re-dealt when a filter changes.
// Room at the left and right of the plot so the newest reading and the end
// date labels are not cut off by the trim they sit on.
const PLOT_INSET = 28;

const CHART_COLORS = [
  '#c04434', '#2b7fbf', '#3f8f5f', '#7a5cc2',
  '#c26a2e', '#4f63b8', '#c25f7e', '#a8871d',
];

/**
 * The usage overview stacked above the app list on the dashboard's My Apps
 * tab. The routed Usage pages (/applications/metrics, os-evals, connections)
 * are ApplicationsPage, not this.
 */
function DeveloperOverview({ groupId = null, analyticsOnly = false }) {
  // Three questions, three tabs. Metrics leads because it is what the page was
  // for before the scores existed, and it is what most visits are actually
  // asking: is anything happening.
  const [tab, setTab] = useState('usage');
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredApp, setHoveredApp] = useState(null);
  // Set of selected app_ids
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Map of app_id → metrics object
  const [metricsMap, setMetricsMap] = useState({});
  const [loadingIds, setLoadingIds] = useState(new Set());

  const chartRef = useRef(null);
  const tooltipRef = useRef(null);
  const detailRef = useRef(null);
  const detailChartRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = groupId
          ? `/third-party/my-apps/overview?group_id=${groupId}`
          : '/third-party/my-apps/overview';
        const result = await api.request(url);
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId]);

  // Fetch metrics for newly selected apps
  const fetchMetrics = useCallback(async (appId) => {
    if (metricsMap[appId]) return;
    setLoadingIds(prev => new Set(prev).add(appId));
    try {
      const result = await api.request(`/third-party/${appId}/metrics`);
      setMetricsMap(prev => ({ ...prev, [appId]: result }));
    } catch {
      setMetricsMap(prev => ({ ...prev, [appId]: null }));
    } finally {
      setLoadingIds(prev => { const s = new Set(prev); s.delete(appId); return s; });
    }
  }, [metricsMap]);

  const handleToggleApp = useCallback((app) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(app.app_id)) {
        next.delete(app.app_id);
      } else {
        next.add(app.app_id);
        fetchMetrics(app.app_id);
      }
      return next;
    });
    setHoveredApp(null);
  }, [fetchMetrics]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setHoveredApp(null);
  }, []);

  // Scroll detail into view when first item selected
  useEffect(() => {
    if (selectedIds.size === 1 && detailRef.current) {
      setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
  }, [selectedIds.size]);

  // ── Overview chart ────────────────────────────────────────────────────────
  const renderChart = useCallback(() => {
    const container = chartRef.current;
    if (!container || !data?.apps.length) return;
    const { width } = container.getBoundingClientRect();
    if (width < 10) return;

    const height = 320;
    const margin = { top: 16, right: 0, bottom: 28, left: 0 };
    d3.select(container).selectAll('svg').remove();
    const svg = d3.select(container).append('svg').attr('width', width).attr('height', height)
      .attr('class', 'dev-chart');
    const iw = width - margin.left - margin.right;
    const ih = height - margin.top - margin.bottom;
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const allDates = d3.timeDays(d3.timeDay.offset(today, -29), d3.timeDay.offset(today, 1));
    const fmt = d3.timeFormat('%Y-%m-%d');

    const series = data.apps.map((app, i) => {
      const dayMap = new Map((app.calls_by_day_30d || []).map(d => [d.date, d.count]));
      return {
        id: app.app_id, name: app.app_name,
        color: CHART_COLORS[i % CHART_COLORS.length],
        values: allDates.map(date => ({ date, count: dayMap.get(fmt(date)) || 0 })),
      };
    });

    // THE PLOT BREATHES AT BOTH ENDS. The range used to run the full width, so
    // the newest point sat exactly on the trim and the first and last date
    // labels — centred on their points — were sliced in half by the container.
    // The rules are inset with it — see the y axis below for why. `clamp` keeps
    // a hover in the inset from inventing a date outside the 30 days.
    const x = d3.scaleTime().domain(d3.extent(allDates)).range([PLOT_INSET, iw - PLOT_INSET]).clamp(true);
    const yMax = d3.max(allDates, date => {
      const ds = fmt(date);
      return d3.sum(series, s => { const e = s.values.find(v => fmt(v.date) === ds); return e ? e.count : 0; });
    });
    const y = d3.scaleLinear().domain([0, yMax || 10]).range([ih, 0]).nice();

    // The rules: solid hairlines rail-to-rail (the page's own seam ink), the
    // y census riding ABOVE its rule like a ledger annotation — no boxed axis.
    g.append('g').attr('class', 'dev-chart__x').attr('transform', `translate(0,${ih})`)
      .call(d3.axisBottom(x).ticks(6).tickFormat(d3.timeFormat('%b %d')).tickSize(0).tickPadding(12))
      .call(g => g.select('.domain').remove());

    // SOLID LINES ARE STRUCTURE; DASHED LINES ARE READINGS.
    // A line that crosses a whole pane uninterrupted belongs to the page's
    // grid, and these were drawn in exactly that voice — solid, in the seam
    // ink, spanning the full width — so the chart's own scale read as the page
    // cutting itself into strips. They are the chart's annotation, not the
    // page's frame: dashed (see .dev-chart__y in the stylesheet), and stopping
    // at the plot's edges rather than the pane's, so the chart floats inside
    // the cell instead of being sliced by it.
    g.append('g').attr('class', 'dev-chart__y')
      .attr('transform', `translate(${PLOT_INSET},0)`)
      .call(d3.axisLeft(y).ticks(4).tickSize(-(iw - PLOT_INSET * 2)).tickFormat(d3.format('~s')).tickPadding(0))
      .call(g => g.select('.domain').remove())
      .call(g => g.selectAll('.tick text')
        .attr('x', 4).attr('dy', -4).attr('text-anchor', 'start'));

    const lineGen = d3.line().x(d => x(d.date)).y(d => y(d.count)).curve(d3.curveMonotoneX);
    const areaGen = d3.area().x(d => x(d.date)).y0(ih).y1(d => y(d.count)).curve(d3.curveMonotoneX);

    const anySelected = selectedIds.size > 0;
    const isActive = id => !anySelected && hoveredApp === null ? true
      : hoveredApp === id || selectedIds.has(id);

    series.forEach(s => {
      g.append('path').datum(s.values).attr('d', areaGen)
        .attr('fill', s.color).attr('fill-opacity', isActive(s.id) ? 0.09 : 0.02);
      g.append('path').datum(s.values).attr('d', lineGen)
        .attr('fill', 'none').attr('stroke', s.color)
        .attr('stroke-width', selectedIds.has(s.id) ? 2.5 : isActive(s.id) ? 2 : 0.6)
        .attr('stroke-opacity', isActive(s.id) ? 1 : 0.18);
    });

    // THE PLOT'S OWN FRAME — dashed, for the same reason its rules are: it
    // bounds a reading rather than dividing the page. Drawn after the series so
    // the enclosure stays legible over the area fills.
    g.append('rect').attr('class', 'dev-chart__frame')
      .attr('x', PLOT_INSET).attr('y', 0)
      .attr('width', Math.max(0, iw - PLOT_INSET * 2)).attr('height', ih)
      .attr('fill', 'none');

    const hoverLine = g.append('line').attr('class', 'dev-chart__crosshair')
      .attr('y1', 0).attr('y2', ih).style('opacity', 0);
    const tooltip = tooltipRef.current;

    g.append('rect').attr('width', iw).attr('height', ih).attr('fill', 'transparent')
      .on('mousemove', function (event) {
        const [mx] = d3.pointer(event);
        const ds = fmt(x.invert(mx));
        hoverLine.attr('x1', mx).attr('x2', mx).style('opacity', 1);
        const visible = anySelected ? series.filter(s => selectedIds.has(s.id)) : series;
        const lines = visible.map(s => {
          const e = s.values.find(v => fmt(v.date) === ds);
          return `<span style="color:${s.color}">●</span> ${s.name}: <strong>${(e?.count || 0).toLocaleString()}</strong>`;
        }).join('<br/>');
        if (tooltip) {
          tooltip.innerHTML = `<strong>${d3.timeFormat('%b %d, %Y')(x.invert(mx))}</strong><br/>${lines}`;
          tooltip.style.opacity = '1';
          tooltip.style.left = `${event.offsetX + 14}px`;
          tooltip.style.top = `${event.offsetY - 14}px`;
        }
      })
      .on('mouseleave', () => { hoverLine.style('opacity', 0); if (tooltip) tooltip.style.opacity = '0'; });
  }, [data, hoveredApp, selectedIds]);

  useEffect(() => { renderChart(); }, [renderChart]);
  useEffect(() => {
    const el = chartRef.current; if (!el) return;
    const ro = new ResizeObserver(() => renderChart());
    ro.observe(el); return () => ro.disconnect();
  }, [renderChart]);

  // ── Detail chart (selected apps overlaid) ────────────────────────────────
  const renderDetailChart = useCallback(() => {
    const container = detailChartRef.current;
    if (!container || !data || selectedIds.size === 0) return;
    const { width } = container.getBoundingClientRect();
    if (width < 10) return;

    const height = 180;
    const margin = { top: 12, right: 0, bottom: 26, left: 0 };
    const iw = width - margin.left - margin.right;
    const ih = height - margin.top - margin.bottom;

    d3.select(container).selectAll('svg').remove();
    const svg = d3.select(container).append('svg').attr('width', width).attr('height', height)
      .attr('class', 'dev-chart');
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const allDates = d3.timeDays(d3.timeDay.offset(today, -29), d3.timeDay.offset(today, 1));
    const fmt = d3.timeFormat('%Y-%m-%d');

    const selectedApps = data.apps.filter(a => selectedIds.has(a.app_id));
    const series = selectedApps.map((app, i) => {
      const idx = data.apps.indexOf(app);
      const src = metricsMap[app.app_id]?.calls_by_day || app.calls_by_day_30d || [];
      const dayMap = new Map(src.map(d => [d.date, d.count]));
      return {
        id: app.app_id, name: app.app_name,
        color: CHART_COLORS[idx % CHART_COLORS.length],
        values: allDates.map(date => ({ date, count: dayMap.get(fmt(date)) || 0 })),
      };
    });

    // THE PLOT BREATHES AT BOTH ENDS. The range used to run the full width, so
    // the newest point sat exactly on the trim and the first and last date
    // labels — centred on their points — were sliced in half by the container.
    // The rules are inset with it — see the y axis below for why. `clamp` keeps
    // a hover in the inset from inventing a date outside the 30 days.
    const x = d3.scaleTime().domain(d3.extent(allDates)).range([PLOT_INSET, iw - PLOT_INSET]).clamp(true);
    const yMax = d3.max(series.flatMap(s => s.values), d => d.count) || 10;
    const y = d3.scaleLinear().domain([0, yMax]).range([ih, 0]).nice();

    g.append('g').attr('class', 'dev-chart__x').attr('transform', `translate(0,${ih})`)
      .call(d3.axisBottom(x).ticks(6).tickFormat(d3.timeFormat('%b %d')).tickSize(0).tickPadding(10))
      .call(g => g.select('.domain').remove());
    // SOLID LINES ARE STRUCTURE; DASHED LINES ARE READINGS.
    // A line that crosses a whole pane uninterrupted belongs to the page's
    // grid, and these were drawn in exactly that voice — solid, in the seam
    // ink, spanning the full width — so the chart's own scale read as the page
    // cutting itself into strips. They are the chart's annotation, not the
    // page's frame: dashed (see .dev-chart__y in the stylesheet), and stopping
    // at the plot's edges rather than the pane's, so the chart floats inside
    // the cell instead of being sliced by it.
    g.append('g').attr('class', 'dev-chart__y')
      .attr('transform', `translate(${PLOT_INSET},0)`)
      .call(d3.axisLeft(y).ticks(4).tickSize(-(iw - PLOT_INSET * 2)).tickFormat(d3.format('~s')).tickPadding(0))
      .call(g => g.select('.domain').remove())
      .call(g => g.selectAll('.tick text')
        .attr('x', 4).attr('dy', -4).attr('text-anchor', 'start'));

    series.forEach(s => {
      g.append('path').datum(s.values)
        .attr('d', d3.area().x(d => x(d.date)).y0(ih).y1(d => y(d.count)).curve(d3.curveMonotoneX))
        .attr('fill', s.color).attr('fill-opacity', 0.08);
      g.append('path').datum(s.values)
        .attr('d', d3.line().x(d => x(d.date)).y(d => y(d.count)).curve(d3.curveMonotoneX))
        .attr('fill', 'none').attr('stroke', s.color).attr('stroke-width', 2);
    });

    // THE PLOT'S OWN FRAME — dashed, for the same reason its rules are: it
    // bounds a reading rather than dividing the page. Drawn after the series so
    // the enclosure stays legible over the area fills.
    g.append('rect').attr('class', 'dev-chart__frame')
      .attr('x', PLOT_INSET).attr('y', 0)
      .attr('width', Math.max(0, iw - PLOT_INSET * 2)).attr('height', ih)
      .attr('fill', 'none');
  }, [data, selectedIds, metricsMap]);

  useEffect(() => { if (selectedIds.size > 0) renderDetailChart(); }, [renderDetailChart, selectedIds]);
  useEffect(() => {
    const el = detailChartRef.current; if (!el || selectedIds.size === 0) return;
    const ro = new ResizeObserver(() => renderDetailChart());
    ro.observe(el); return () => ro.disconnect();
  }, [renderDetailChart, selectedIds]);

  // Always show the skeleton while loading, so the overview reserves its space
  // and shimmers in place instead of popping in above the app list.
  if (loading || !data) return loading ? <LoadingSkeleton /> : null;
  // Standalone empty state only when NOT embedded — in My Applications the stat
  // cards (zeros) + Register button render instead, and the app manager owns the
  // "no apps" list state below.
  const isEmptyOverview = data.summary.total_apps === 0 && data.summary.total_plugins === 0;
  if (isEmptyOverview && !analyticsOnly) {
    return (
      <EmptyState
        message="You haven't registered any apps or plugins yet."
        action={<Button variant="primary" onClick={() => navigate('/create')}>Register Your First App</Button>}
      />
    );
  }

  const { summary, apps = [] } = data;
  const selectedApps = apps.filter(a => selectedIds.has(a.app_id));
  const isSingle = selectedIds.size === 1;

  const chartTitle = selectedIds.size === 0 ? 'API Usage'
    : isSingle ? `API Usage — ${selectedApps[0].app_name}`
    : `API Usage — ${selectedIds.size} apps`;

  // The sections take turns in one pane — whichever is standing, it is the same
  // markup in either presentation.
  const body = (
    <>
      {/* No pad — the fleet table is full bleed, trim to trim. */}
      {tab === 'scores' && <FleetScores groupId={groupId} />}
      {tab === 'connections' && <FleetScores groupId={groupId} mode="connections" />}

      {tab === 'usage' && (<>

      {apps.some(a => a.total_calls_30d > 0) && (
        <section className="dev-overview__chart-band">
          {/* The caption band — a ruled ledger line, not a card header. */}
          <div className="dev-overview__caption">
            <span>{chartTitle}</span>
            <span className="dev-overview__caption-meta">Last 30 days</span>
          </div>
          <div className="dev-overview__chart" ref={chartRef}>
            <div className="dev-overview__tooltip" ref={tooltipRef} style={{ opacity: 0 }} />
          </div>
          {/* The legend IS a strip of equidistant cells — press to isolate. */}
          <div className="dev-overview__legend" role="group" aria-label="Apps">
            {apps.map((app, i) => {
              const isSelected = selectedIds.has(app.app_id);
              const isDimmed = selectedIds.size > 0 && !isSelected && hoveredApp !== app.app_id;
              return (
                <button
                  key={app.app_id}
                  type="button"
                  className="dev-overview__legend-cell"
                  data-active={isSelected || undefined}
                  data-dimmed={isDimmed || undefined}
                  onMouseEnter={() => { if (selectedIds.size === 0) setHoveredApp(app.app_id); }}
                  onMouseLeave={() => { if (selectedIds.size === 0) setHoveredApp(null); }}
                  onClick={() => handleToggleApp(app)}
                >
                  <span className="dev-overview__legend-dot" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                  {app.app_name}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* THE COUNTERS — one ruled row, cells splitting the full width. */}
      <div className="dev-overview__stats">
        <StatCell label="Apps" value={summary.total_apps} />
        <StatCell label="Plugins" value={summary.total_plugins} />
        <StatCell label="Authorized users" value={summary.total_authorized_users} />
        <StatCell label="API calls (30d)" value={summary.total_api_calls_30d} />
      </div>

      <section>
        <div className="dev-overview__caption">
          <span>Your apps</span>
          {/* My Applications left the sidebar (owner call 2026-08-14) — the
              apps hub is reached from here now. */}
          <button
            type="button"
            className="dev-overview__caption-link"
            onClick={() => navigate('/applications/all')}
          >
            My Applications →
          </button>
        </div>
        <PlateGrid min={240} className="dev-overview__apps-grid">
          {apps.map((app, i) => (
            <AppPlate
              key={app.app_id}
              app={app}
              color={CHART_COLORS[i % CHART_COLORS.length]}
              selected={selectedIds.has(app.app_id)}
              dimmed={selectedIds.size > 0 && !selectedIds.has(app.app_id)}
              onClick={() => handleToggleApp(app)}
            />
          ))}
        </PlateGrid>
      </section>

      {/* Detail panel */}
      {selectedIds.size > 0 && (
        <div className="dev-overview__detail" ref={detailRef}>
          <div className="dev-overview__detail-header">
            <div>
              <div className="dev-overview__detail-name">
                {isSingle ? selectedApps[0].app_name : `Comparing ${selectedIds.size} apps`}
              </div>
              <div className="dev-overview__detail-meta">30-day breakdown</div>
            </div>
            <button className="dev-overview__detail-close" onClick={clearSelection}>✕</button>
          </div>

          {/* Comparison stat rows — one per app */}
          <div className="dev-overview__detail-compare-rows">
            {selectedApps.map((app) => {
              const idx = apps.indexOf(app);
              const color = CHART_COLORS[idx % CHART_COLORS.length];
              const todayCalls = (app.calls_by_day_30d?.slice(-1)[0]?.count ?? 0);
              const metrics = metricsMap[app.app_id];
              const topEndpoint = metrics?.calls_by_endpoint?.[0]?.endpoint;
              return (
                <div key={app.app_id} className="dev-overview__detail-compare-row" style={{ borderLeftColor: color }}>
                  <div className="dev-overview__detail-compare-name">
                    <span className="dev-overview__detail-color-dot" style={{ background: color }} />
                    {app.app_name}
                  </div>
                  <div className="dev-overview__detail-compare-stats">
                    <DetailStat value={app.authorized_users_count} label="Users" color={color} />
                    <DetailStat value={app.total_calls_30d.toLocaleString()} label="Calls (30d)" color={color} />
                    <DetailStat value={todayCalls.toLocaleString()} label="Today" color={color} />
                    {topEndpoint && <DetailStat value={topEndpoint} label="Top Endpoint" color={color} small />}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Overlaid chart */}
          <div className="dev-overview__detail-chart-wrap">
            <div ref={detailChartRef} className="dev-overview__detail-chart" />
          </div>

          {/* Per-app breakdown blocks */}
          {selectedApps.map((app) => {
            const idx = apps.indexOf(app);
            const color = CHART_COLORS[idx % CHART_COLORS.length];
            const metrics = metricsMap[app.app_id];
            const isLoading = loadingIds.has(app.app_id);
            return (
              <div key={app.app_id} className="dev-overview__detail-app-block">
                <div className="dev-overview__detail-app-block-header" style={{ borderLeftColor: color, '--app-color': color }}>
                  <span className="dev-overview__detail-color-dot" style={{ background: color }} />
                  {app.app_name}
                </div>
                <div className="dev-overview__detail-grid">
                  <EndpointTable rows={metrics?.calls_by_endpoint || []} loading={isLoading} color={color} />
                  <TopUsersTable rows={metrics?.top_users || []} loading={isLoading} color={color} />
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>)}
    </>
  );

  return (
    <div className="dev-overview">
      {/* THE SECTION STRIP — full width, equidistant cells, seams only where
          two cells meet (the sidebar's own grammar, turned horizontal). */}
      <div className="bandstrip" role="tablist" aria-label="Overview sections">
        {SECTION_NAMES.map(({ id, name }) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            data-active={tab === id || undefined}
            className="bandstrip__tab"
            onClick={() => setTab(id)}
          >
            {name}
          </button>
        ))}
      </div>
      {body}
    </div>
  );
}

// One counter cell of the ruled row. The number wears the text ink (color
// carries identity in this page, never magnitude).
function StatCell({ label, value }) {
  return (
    <div className="dev-overview__stat">
      <div className="dev-overview__stat-value">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="dev-overview__stat-label">{label}</div>
    </div>
  );
}

function DetailStat({ value, label, color, small }) {
  return (
    <div className="dev-overview__detail-stat">
      <div className="dev-overview__detail-stat-value" style={{ color, fontSize: small ? '14px' : undefined, letterSpacing: small ? 0 : undefined }}>
        {value}
      </div>
      <div className="dev-overview__detail-stat-label">{label}</div>
    </div>
  );
}

function EndpointTable({ rows, loading, color }) {
  const sorted = [...rows].sort((a, b) => b.count - a.count).slice(0, 8);
  const max = sorted[0]?.count || 1;
  return (
    <div className="dev-overview__detail-table-section">
      <div className="dev-overview__detail-section-label">Endpoint Breakdown</div>
      {loading ? <div className="dev-overview__detail-loading">Loading…</div> : (
        <table className="dev-overview__detail-table">
          <thead><tr><th>Endpoint</th><th>Calls</th><th></th></tr></thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={3} className="dev-overview__detail-empty">No data</td></tr>}
            {sorted.map((e, i) => (
              <tr key={i}>
                <td><span className={`dev-overview__method dev-overview__method--${e.method?.toLowerCase()}`}>{e.method}</span>{e.endpoint}</td>
                <td>{e.count?.toLocaleString()}</td>
                <td className="dev-overview__bar-cell">
                  <div className="dev-overview__bar-bg">
                    <div className="dev-overview__bar-fill" style={{ width: `${Math.round(e.count / max * 100)}%`, background: color }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TopUsersTable({ rows, loading, color }) {
  const sorted = [...rows].sort((a, b) => b.calls - a.calls).slice(0, 7);
  const max = sorted[0]?.calls || 1;
  return (
    <div className="dev-overview__detail-table-section">
      <div className="dev-overview__detail-section-label">Top Users</div>
      {loading ? <div className="dev-overview__detail-loading">Loading…</div> : (
        <table className="dev-overview__detail-table">
          <thead><tr><th>User</th><th>Calls</th><th></th></tr></thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={3} className="dev-overview__detail-empty">No data</td></tr>}
            {sorted.map((u, i) => (
              <tr key={i}>
                <td>{u.username || u.user_id?.slice(0, 8)}</td>
                <td>{u.calls?.toLocaleString()}</td>
                <td className="dev-overview__bar-cell">
                  <div className="dev-overview__bar-bg">
                    <div className="dev-overview__bar-fill" style={{ width: `${Math.round(u.calls / max * 100)}%`, background: color }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// One app as a data PLATE (the kit's seamed grid of opaque cells): name +
// kind, the two counters, the 30-day sparkline riding the plate's foot.
function AppPlate({ app, color, selected, dimmed, onClick }) {
  const sparkRef = useRef(null);
  useEffect(() => {
    const el = sparkRef.current;
    if (!el || !app.calls_by_day_30d?.length) return;
    const { width } = el.getBoundingClientRect();
    const h = 32;
    d3.select(el).selectAll('svg').remove();
    const svg = d3.select(el).append('svg').attr('width', width).attr('height', h);
    const vals = app.calls_by_day_30d.map(d => d.count);
    const x = d3.scaleLinear().domain([0, vals.length - 1]).range([0, width]);
    const y = d3.scaleLinear().domain([0, d3.max(vals) || 1]).range([h - 2, 2]);
    svg.append('path').datum(vals)
      .attr('d', d3.area().x((_, i) => x(i)).y0(h).y1(d => y(d)).curve(d3.curveMonotoneX))
      .attr('fill', color).attr('fill-opacity', 0.1);
    svg.append('path').datum(vals)
      .attr('d', d3.line().x((_, i) => x(i)).y(d => y(d)).curve(d3.curveMonotoneX))
      .attr('fill', 'none').attr('stroke', color).attr('stroke-width', 1.5);
  }, [app.calls_by_day_30d, color]);

  return (
    <Plate
      className="dev-overview__app-plate"
      selected={selected}
      data-dimmed={dimmed || undefined}
      onClick={onClick}
    >
      <div className="ofself-plate__name dev-overview__app-plate-head">
        <span className="dev-overview__app-plate-dot" style={{ background: color }} />
        {app.app_name}
        <Badge variant={app.is_plugin ? 'info' : 'default'}>{app.is_plugin ? 'Plugin' : 'App'}</Badge>
      </div>
      <div className="ofself-plate__foot dev-overview__app-plate-stats">
        <span><strong>{app.authorized_users_count}</strong> users</span>
        <span><strong>{app.total_calls_30d.toLocaleString()}</strong> calls (30d)</span>
      </div>
      {app.calls_by_day_30d?.length > 1 && <div className="dev-overview__app-plate-spark" ref={sparkRef} />}
    </Plate>
  );
}

// The overview's three sections, named in the strip.
const SECTION_NAMES = [
  { id: 'usage', name: 'Metrics' },
  { id: 'scores', name: 'Scores' },
  { id: 'connections', name: 'Connections' },
];

function LoadingSkeleton() {
  return (
    <div className="dev-overview dev-overview--loading" aria-busy="true" aria-label="Loading overview">
      <div className="dev-overview__skeleton-block" style={{ height: 44 }} />
      <div className="dev-overview__skeleton-block" style={{ height: 320 }} />
      <div className="dev-overview__skeleton-row">
        {[1, 2, 3, 4].map(i => <div key={i} className="dev-overview__skeleton-block" style={{ height: 96 }} />)}
      </div>
      <div className="dev-overview__skeleton-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {[1, 2, 3].map(i => <div key={i} className="dev-overview__skeleton-block" style={{ height: 120 }} />)}
      </div>
    </div>
  );
}

export default DeveloperOverview;
